import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { newsArticles } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetail() {
  const { id } = useParams();
  const article = newsArticles.find((item) => item.id === id);

  usePageMeta({
    title: article ? `${article.title}-色戒完整版无删减hd影视资讯` : "影视资讯详情-色戒完整版无删减hd",
    description: article
      ? article.summary
      : "该资讯内容不存在或已删除，可返回影视资讯列表浏览其他文章。",
    keywords: article
      ? `${article.category},${article.title},影视资讯,电影幕后,色戒完整版无删减hd`
      : "影视资讯,电影新闻",
  });

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = newsArticles.filter(
      (item) => item.id !== article.id && item.category === article.category,
    );
    const others = newsArticles.filter(
      (item) => item.id !== article.id && item.category !== article.category,
    );
    return [...sameCategory, ...others].slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <PageShell>
        <div className="mx-auto max-w-shell px-4 py-16 md:px-6 md:py-24">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯", to: "/news" }, { label: "内容不存在" }]} />
          <div className="mt-8">
            <EmptyState
              icon="ri-file-unknow-line"
              title="内容不存在"
              description="这篇资讯可能已经下线，或者链接地址有误。返回资讯列表可以继续浏览其他文章。"
              actionLabel="返回资讯列表"
              onAction={() => {
                window.REACT_APP_NAVIGATE?.("/news");
              }}
              secondaryLabel="回到首页"
              onSecondary={() => {
                window.REACT_APP_NAVIGATE?.("/");
              }}
            />
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影视资讯", to: "/news" },
            { label: article.category },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <article className="min-w-0 lg:col-span-8">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-50 px-3 py-1 text-[11px] font-medium text-accent-800">
              <i className="ri-price-tag-3-line" />
              {article.category}
            </span>
            <h1 className="mt-4 font-heading text-[24px] font-bold leading-snug text-foreground-950 md:text-[32px]">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-5 text-[11px] text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                发布于 {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                约 3 分钟阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                影视资讯频道
              </span>
            </div>

            <p className="mt-6 rounded-lg border-l-2 border-accent-500 bg-background-100 px-4 py-3 text-sm leading-relaxed text-foreground-700">
              {article.summary}
            </p>

            <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <img
                src={article.cover}
                alt={`${article.title} 资讯配图`}
                title={`${article.title} · 影视资讯`}
                className="aspect-[16/10] w-full object-top"
              />
            </div>

            <div className="mt-6">
              {article.paragraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className="mb-5 text-sm leading-[2] text-foreground-700 last:mb-0 md:text-[15px]"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-4">
              <Link
                to="/news"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回资讯列表
              </Link>
              <Link
                to="/category"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
              >
                浏览影视分类
              </Link>
              <span className="ml-auto flex items-center gap-1 text-[11px] text-foreground-400">
                <i className="ri-information-line" />
                演示内容
              </span>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="sticky top-[88px]">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                  <i className="ri-article-line text-accent-600" />
                  相关资讯
                </h2>
                <div className="mt-3 space-y-3">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/news/${item.id}`}
                      className="group flex gap-3 rounded-md p-1.5 transition-colors hover:bg-background-100"
                    >
                      <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title} 缩略图`}
                          title={item.title}
                          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="clamp-2 text-xs leading-relaxed text-foreground-800 transition-colors group-hover:text-accent-600">
                          {item.title}
                        </p>
                        <time className="mt-1 block font-label text-[10px] text-foreground-400">
                          {item.date}
                        </time>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </PageShell>
  );
}